import { onScopeDispose } from 'vue'
import { useStore } from 'vuex'

import PowerBiApiHandler from '@/api/power-bi'
import { handleError } from '@/utils/error-handling'
import {
  POWER_BI_AZURE_PERMISSIONS_SCOPE,
  POWER_BI_AZURE_POPUP_NAME,
  POWER_BI_KEYS
} from '@/utils/power-bi'
import { generatePowerBiAuthUrl } from '@/utils/power-bi'

const DEFAULT_POPUP_HEIGHT = 800
const DEFAULT_POPUP_WIDTH = 600
const getMsAuthPopupParams = ({
  height = DEFAULT_POPUP_HEIGHT,
  width = DEFAULT_POPUP_WIDTH
} = {}) => {
  const left = Math.round((window.screen.width - width) / 2)
  const top = Math.round((window.screen.height - height) / 2)
  const adjustedLeft = window.screenX + left
  const adjustedTop = window.screenY + top

  return `width=${width},height=${height},left=${adjustedLeft},top=${adjustedTop}`
}

const MS_AUTH_POPUP_CHECK_INTERVAL = 500 // 500ms

export const useMsAuth = () => {
  const powerBiApi = new PowerBiApiHandler()

  let popupClosedCheckIntervalId = null
  const clearPopupClosedCheckIntervalId = () => {
    if (popupClosedCheckIntervalId) {
      clearInterval(popupClosedCheckIntervalId)
      popupClosedCheckIntervalId = null
    }
  }

  const store = useStore()

  const createPowerBiOAuthUrlOptions = (state = '') => {
    return {
      [POWER_BI_KEYS.CLIENT_ID]: process.env.VUE_APP_POWER_BI_AZURE_APP_ID,
      [POWER_BI_KEYS.REDIRECT_URI]: process.env.VUE_APP_POWER_BI_MS_LOGIN_REDIRECT_URI,
      [POWER_BI_KEYS.SCOPE]: POWER_BI_AZURE_PERMISSIONS_SCOPE,
      [POWER_BI_KEYS.STATE]: state
    }
  }

  const connectPowerBI = () => {
    store.dispatch('system/toggleFullAppLoader', {
      flag: true
    })
    return new Promise((resolve, reject) => {
      powerBiApi
        .getLoginToken()
        .then(data => {
          const oAuthOptions = createPowerBiOAuthUrlOptions(JSON.stringify(data))
          const url = generatePowerBiAuthUrl(oAuthOptions)

          const popupParams = getMsAuthPopupParams()
          const popup = window.open(url, POWER_BI_AZURE_POPUP_NAME, popupParams)

          if (!popup) {
            return reject(new Error('Failed to open authentication popup'))
          }

          popupClosedCheckIntervalId = setInterval(() => {
            if (popup && popup.closed) {
              clearPopupClosedCheckIntervalId()
              return resolve()
            }
          }, MS_AUTH_POPUP_CHECK_INTERVAL)
        })
        .catch(error => {
          reject(error)
        })
    })
      .catch(error => {
        handleError({ error })
        throw error
      })
      .finally(() => {
        store.dispatch('system/toggleFullAppLoader', {
          flag: false
        })
      })
  }

  onScopeDispose(() => {
    clearPopupClosedCheckIntervalId()
  })

  return { connectPowerBI }
}
